import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@parcel/ui';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Filter, HeartOff, List } from 'lucide-react';
import * as React from 'react';
import {
  favoriteCollapseKey,
  favoriteDatasetLabel,
  favoriteViewTo,
  readFavoriteCollapsed,
  writeFavoriteCollapsed,
} from '../lib/favorite-views-state.js';
import type { WorkspaceView } from '../lib/workspace-views.js';

export type FavoriteViewsSectionProps = {
  favorites: readonly WorkspaceView[];
  /** The favorited View the URL is currently showing, highlighted in the list. */
  activeViewId: string | null;
  workspaceSlug: string;
  userId: string;
  workspaceId: string;
  onUnfavorite: (viewId: string) => void;
  onReorder: (viewIds: readonly string[]) => void;
};

export function FavoriteViewsSection({
  favorites,
  activeViewId,
  workspaceSlug,
  userId,
  workspaceId,
  onUnfavorite,
  onReorder,
}: FavoriteViewsSectionProps) {
  const collapseKey = favoriteCollapseKey(userId, workspaceId);
  const [collapsed, setCollapsed] = React.useState(false);
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    setCollapsed(readFavoriteCollapsed(window.localStorage, collapseKey));
    setHydrated(true);
  }, [collapseKey]);

  const sensors = useSensors(
    // The row itself is the drag handle now, and that row is a link. Without a distance
    // threshold every click would begin a drag and swallow the navigation; 8px is far
    // enough that an ordinary click never crosses it and a deliberate drag always does.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Space only, never Enter. dnd-kit starts on both by default, which would hijack
    // Enter on the link and make Favorites unreachable by keyboard. Browsers do not
    // activate links on Space, so Space is free to mean "pick up".
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Tab'] },
    }),
  );

  const ids = favorites.map((view) => view.id);

  const announcements = React.useMemo(() => {
    const getPosition = (id: string | number) => ids.indexOf(String(id)) + 1;
    const count = ids.length;
    const nameFor = (id: string | number) =>
      favorites.find((view) => view.id === String(id))?.name ?? String(id);

    return {
      onDragStart({ active }: { active: { id: string | number } }) {
        return `Picked up ${nameFor(active.id)}. ${nameFor(active.id)} is in position ${getPosition(active.id)} of ${count}`;
      },
      onDragOver({
        active,
        over,
      }: { active: { id: string | number }; over: { id: string | number } | null }) {
        if (!over) return;
        return `${nameFor(active.id)} was moved into position ${getPosition(over.id)} of ${count}`;
      },
      onDragEnd({
        active,
        over,
      }: { active: { id: string | number }; over: { id: string | number } | null }) {
        if (!over) return;
        return `${nameFor(active.id)} was dropped at position ${getPosition(over.id)} of ${count}`;
      },
      onDragCancel({ active }: { active: { id: string | number } }) {
        return `Sorting was cancelled. ${nameFor(active.id)} returned to position ${getPosition(active.id)} of ${count}`;
      },
    };
  }, [favorites, ids]);

  /**
   * Set the instant a drag finishes, consumed by the click that the browser fires next.
   *
   * The row is an anchor, and pointerup is always followed by a click on it, so without
   * this every drop also navigated to whichever View was dragged. dnd-kit calls onDragEnd
   * synchronously while handling pointerup, which is what makes a plain ref enough: it is
   * already true by the time the click arrives, with no timers and nothing to race.
   *
   * Declared above the zero-Favorites return, with every other hook. Below it, favoriting
   * a first View or unfavoriting a last one changes the hook count between renders and
   * React throws.
   */
  const suppressClickRef = React.useRef(false);

  /**
   * Cancel the click that follows a drop, on `window` in the capture phase.
   *
   * It has to be here and nowhere else. When a drag starts, dnd-kit adds its own click
   * listener on `document` with capture, and that listener calls stopPropagation and
   * nothing else (AbstractPointerSensor.handleStart). Two things follow. The event never
   * reaches React's root, so no onClickCapture anywhere in this tree can see it. And
   * stopPropagation does not cancel a default action, so the anchor still navigates, which
   * is a full page load that also kills the reorder request mid-flight. Capture on `window`
   * runs before capture on `document`, so this fires first, and preventDefault is what
   * actually stops the navigation.
   *
   * pointerdown clears the flag so a keyboard drag, which ends with no click at all, cannot
   * leave it set and swallow the next real click.
   */
  React.useEffect(() => {
    const swallowClick = (event: MouseEvent) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };
    const clearPending = () => {
      suppressClickRef.current = false;
    };
    window.addEventListener('click', swallowClick, true);
    window.addEventListener('pointerdown', clearPending, true);
    return () => {
      window.removeEventListener('click', swallowClick, true);
      window.removeEventListener('pointerdown', clearPending, true);
    };
  }, []);

  if (favorites.length === 0) return null;

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      if (hydrated) {
        writeFavoriteCollapsed(window.localStorage, collapseKey, next);
      }
      return next;
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    // Before the early return: a drag that lands back where it started still must not
    // navigate, because the user was dragging, not clicking.
    suppressClickRef.current = true;
    if (!over || active.id === over.id) return;
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    onReorder(next);
  };

  return (
    <SidebarGroup className="pt-0">
      <SidebarGroupLabel asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand Favorites' : 'Collapse Favorites'}
          onClick={toggleCollapsed}
        >
          <span className="flex-1 text-left">Favorites</span>
          {collapsed ? (
            <ChevronRight className="size-4" aria-hidden />
          ) : (
            <ChevronDown className="size-4" aria-hidden />
          )}
        </button>
      </SidebarGroupLabel>
      {collapsed ? null : (
        <SidebarGroupContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            accessibility={{ announcements }}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
              suppressClickRef.current = true;
            }}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {/* No click handling here. dnd-kit stops the post-drop click at document
                  capture, so nothing React renders can observe it; the window listener
                  above is the only place that runs first. */}
              <SidebarMenu>
                {favorites.map((view) => (
                  <FavoriteViewRow
                    key={view.id}
                    view={view}
                    isActive={view.id === activeViewId}
                    workspaceSlug={workspaceSlug}
                    onUnfavorite={onUnfavorite}
                  />
                ))}
              </SidebarMenu>
            </SortableContext>
          </DndContext>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

function FavoriteViewRow({
  view,
  isActive,
  workspaceSlug,
  onUnfavorite,
}: {
  view: WorkspaceView;
  isActive: boolean;
  workspaceSlug: string;
  onUnfavorite: (viewId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    activeIndex,
    index,
  } = useSortable({ id: view.id });

  const TypeIcon = view.view_type === 'dynamic' ? Filter : List;
  const datasetLabel = favoriteDatasetLabel(view.dataset);

  // Brand-green insertion line on the row under the cursor, drawn on the edge the dragged
  // row is travelling toward: coming up the list it lands above this row, going down it
  // lands below. The row being dragged never draws one against itself.
  const dropEdge =
    !isOver || isDragging || activeIndex === index ? null : activeIndex > index ? 'top' : 'bottom';

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      className="relative"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : undefined,
      }}
    >
      {dropEdge ? (
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-x-1 z-10 h-0.5 rounded-full bg-brand ${
            dropEdge === 'top' ? '-top-0.5' : '-bottom-0.5'
          }`}
        />
      ) : null}
      <div className="flex w-full min-w-0 items-center">
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={view.name}
          className="min-w-0 flex-1"
        >
          {/*
            The whole row is the drag handle, which is why there is no grip: the grip cost
            a fixed 24px of a narrow sidebar and pushed every View name into truncation.
            Spread `listeners` but not `attributes`, because attributes carries
            role="button" and would strip this anchor of its link semantics. The two aria
            hooks below are the parts of attributes worth keeping: they announce the row as
            sortable and point at dnd-kit's generated instructions.
          */}
          <Link
            ref={setActivatorNodeRef}
            to={favoriteViewTo(workspaceSlug, view)}
            search={{ view: view.id }}
            // THIS is what was reloading the whole app. An anchor with an href is natively
            // draggable, so pressing and moving on it started the browser's own HTML5 link
            // drag alongside dnd-kit's. Dropping that anywhere in the window is a native
            // navigation to the dragged URL, which is a full page load, not a client-side
            // route change. The old grip never hit this because a <button> is not natively
            // draggable. dnd-kit drives off pointer events, so switching this off costs it
            // nothing.
            draggable={false}
            // dnd-kit's pointer sensor needs the browser to stop claiming these gestures
            // for scrolling and text selection, or a drag on touch and trackpad fights it.
            style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
            aria-roledescription={attributes['aria-roledescription']}
            aria-describedby={attributes['aria-describedby']}
            {...listeners}
          >
            <TypeIcon aria-hidden />
            <span className="min-w-0 truncate">
              {view.name}
              <span className="text-muted-foreground">
                {' · '}
                {datasetLabel}
              </span>
            </span>
          </Link>
        </SidebarMenuButton>
        <SidebarMenuAction
          showOnHover
          className="text-muted-foreground peer-hover/menu-button:text-muted-foreground"
          aria-label={`Remove ${view.name} from Favorites`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onUnfavorite(view.id);
          }}
        >
          {/* Heart-off only. The button is invisible until the row is hovered, so the one
              moment it is ever on screen is the moment the user is reaching for it, and at
              that moment the useful thing to show is what the click does. */}
          <HeartOff aria-hidden />
        </SidebarMenuAction>
      </div>
    </SidebarMenuItem>
  );
}
